import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getDownloadURL,
  getMetadata,
  listAll,
  ref,
  StorageReference,
} from 'firebase/storage';
import { INVOICES_PREFIX, detectCategory, parseDateFromFilename, buildGroups } from '../utils/invoices';
import { GroupTree, InvoiceFile, Category } from '../types/invoices';
import { storage } from '../../../firebase';

export const useInvoices = () => {
  const [files, setFiles] = useState<InvoiceFile[]>([]);
  const [groups, setGroups] = useState<GroupTree>({ gifts: {}, workshops: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const baseRef = ref(storage, INVOICES_PREFIX);
      const res = await listAll(baseRef);

      const toInvoice = async (itemRef: StorageReference): Promise<InvoiceFile | null> => {
        try {
          const [meta, url] = await Promise.all([getMetadata(itemRef), getDownloadURL(itemRef)]);
          const name = meta.name;
          const category = detectCategory(name);
          const updated = meta.updated ? new Date(meta.updated) : undefined;
          const size = meta.size;
          const dateFromName = parseDateFromFilename(name);
          const year = dateFromName ? String(dateFromName.getFullYear()) : undefined;
          const month = dateFromName ? String(dateFromName.getMonth() + 1).padStart(2, '0') : undefined;

          return {
            name,
            fullPath: meta.fullPath ?? itemRef.fullPath,
            url,
            updated,
            size,
            dateFromName,
            category,
            year,
            month,
          };
        } catch {
          return null;
        }
      };

      const results = await Promise.allSettled(res.items.map(toInvoice));
      const flat: InvoiceFile[] = results
        .map((r) => (r.status === 'fulfilled' ? r.value : null))
        .filter((x): x is InvoiceFile => !!x);

      setFiles(flat);
      setGroups(buildGroups(flat));
    } catch (e: any) {
      setError(e?.message ?? 'Impossible de charger les factures.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const countByCategory = useMemo(() => {
    const count = (cat: Category) =>
      Object.values(groups[cat]).reduce((acc, months) => acc + Object.values(months).reduce((s, arr) => s + arr.length, 0), 0);
    return { gifts: count('gifts'), workshops: count('workshops') };
  }, [groups]);

  return { files, groups, loading, error, fetchInvoices, countByCategory };
};
